/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package repository

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.serialization.Serializable
import models.TransactionContext

/**
 * API DTOs for context sync
 */
@Serializable
data class ContextListResponse(val contexts: List<ContextApiModel>)

@Serializable
data class ContextApiModel(
    val id: String,
    val user_id: String,
    val name: String,
    val color: String,
    val created_at: String = "",
    val updated_at: String = ""
)

@Serializable
data class CreateContextRequest(val name: String)

@Serializable
data class UpdateContextRequest(
    val name: String? = null,
    val color: String? = null
)

@Serializable
data class SetTransactionContextsRequest(val contextIds: List<String>)

@Serializable
data class BatchAssignContextsRequest(
    val transactionIds: List<String>,
    val contextIds: List<String>
)

@Serializable
data class DeleteContextResponse(val deletedCount: Long)

@Serializable
data class BatchAssignResponse(val updated: Int)

/**
 * HTTP API client for syncing transaction contexts with the server.
 */
class ContextSyncService(
    private val baseUrl: String = "http://localhost:3000",
    private val httpClient: HttpClient,
    private val getAuthToken: suspend () -> String? = { null }
) {

    private suspend fun HttpRequestBuilder.addAuthHeader() {
        getAuthToken()?.let { token ->
            header("Authorization", "Bearer $token")
        }
    }

    /**
     * Fetch all contexts for the authenticated user.
     */
    suspend fun fetchContexts(): Result<List<TransactionContext>> {
        return try {
            val response = httpClient.get("$baseUrl/api/contexts") {
                addAuthHeader()
            }

            if (response.status.isSuccess()) {
                val body: ContextListResponse = response.body()
                Result.success(body.contexts.map { it.toTransactionContext() })
            } else {
                Result.failure(Exception("Server returned ${response.status}: ${response.bodyAsText()}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Create a new context on the server.
     */
    suspend fun createContext(name: String): Result<TransactionContext> {
        return try {
            val response = httpClient.post("$baseUrl/api/contexts") {
                contentType(ContentType.Application.Json)
                addAuthHeader()
                setBody(CreateContextRequest(name = name))
            }

            if (response.status.isSuccess()) {
                val body: ContextApiModel = response.body()
                Result.success(body.toTransactionContext())
            } else {
                Result.failure(Exception("Server returned ${response.status}: ${response.bodyAsText()}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Update an existing context on the server.
     */
    suspend fun updateContext(contextId: String, name: String?, color: String?): Result<TransactionContext> {
        return try {
            val response = httpClient.put("$baseUrl/api/contexts/$contextId") {
                contentType(ContentType.Application.Json)
                addAuthHeader()
                setBody(UpdateContextRequest(name = name, color = color))
            }

            if (response.status.isSuccess()) {
                val body: ContextApiModel = response.body()
                Result.success(body.toTransactionContext())
            } else {
                Result.failure(Exception("Server returned ${response.status}: ${response.bodyAsText()}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Delete a context on the server. Returns number of deleted transaction associations.
     */
    suspend fun deleteContext(contextId: String): Result<Long> {
        return try {
            val response = httpClient.delete("$baseUrl/api/contexts/$contextId") {
                addAuthHeader()
            }

            if (response.status.isSuccess()) {
                val body: DeleteContextResponse = response.body()
                Result.success(body.deletedCount)
            } else {
                Result.failure(Exception("Server returned ${response.status}: ${response.bodyAsText()}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Set contexts for a single transaction (replaces existing).
     */
    suspend fun setTransactionContexts(transactionId: String, contextIds: List<String>): Result<Unit> {
        return try {
            val response = httpClient.put("$baseUrl/api/transactions/$transactionId/contexts") {
                contentType(ContentType.Application.Json)
                addAuthHeader()
                setBody(SetTransactionContextsRequest(contextIds = contextIds))
            }

            if (response.status.isSuccess()) {
                Result.success(Unit)
            } else {
                Result.failure(Exception("Server returned ${response.status}: ${response.bodyAsText()}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Batch-assign contexts to multiple transactions.
     */
    suspend fun batchAssignContexts(transactionIds: List<String>, contextIds: List<String>): Result<Int> {
        return try {
            val response = httpClient.put("$baseUrl/api/transactions/batch/contexts") {
                contentType(ContentType.Application.Json)
                addAuthHeader()
                setBody(BatchAssignContextsRequest(transactionIds = transactionIds, contextIds = contextIds))
            }

            if (response.status.isSuccess()) {
                val body: BatchAssignResponse = response.body()
                Result.success(body.updated)
            } else {
                Result.failure(Exception("Server returned ${response.status}: ${response.bodyAsText()}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}

private fun ContextApiModel.toTransactionContext() = TransactionContext(
    id = id,
    userId = user_id,
    name = name,
    color = color,
    createdAt = created_at,
    updatedAt = updated_at
)
